import { API_TBase } from "./API";
import { TNullBool } from "./nullbool";

export type API_TRoleTemplate = {
  [key: string]: any;
  id: number;
  company_id: number;
  creation_user_flow_id: number;
  job_template_title: string;
  soc_group_number: number;
  soc_group_name: string;
  job_title_eligible_for_sw: string;
  basic_salary: number;
  hours_work: number;
  job_title_shortage_list_eng: string;
  job_title_shortage_list_wal: string;
  job_title_shortage_list_scot: string;
  job_title_shortage_list_nir: string;
  job_title_eligible_for_phd: string;
  job_title: string;
  if_registration_required: string;
  cos_expected_duration: number;
  reporting_to: string;
  duty_1: string;
  duty_2: string;
  duty_3: string;
  duty_4: string;
  duty_5: string;
  duty_6: string;
  duty_7: string;
  duty_8: string;
  qualification: string;
  qualification_other: string;
  if_phd_required: string;
  experience: string;
  skill_1: string;
  skill_2: string;
  skill_3: string;
  skill_4: string;
  skill_5: string;
  skill_6: string;
  skill_7: string;
  skill_8: string;
  if_made_redundant: string;
  completed: boolean;
  soc_code_eligible_for_outside: TNullBool;
} & API_TBase;

export type TRoleTemplate_Table = {
  [key: string]: any;
  id: number;
  company_id: string;
  creation_user_flow_id: string;
  job_template_title: string;
  soc_group_number: string;
  soc_group_name: string;
  job_title_eligible_for_sw: boolean;
  basic_salary: string;
  hours_work: string;
  job_title_shortage_list_eng: string;
  job_title_shortage_list_wal: string;
  job_title_shortage_list_scot: string;
  job_title_shortage_list_nir: string;
  job_title_eligible_for_phd: boolean;
  job_title: string;
  if_registration_required: boolean;
  cos_expected_duration: string;
  reporting_to: string;
  duty_1: string;
  duty_2: string;
  duty_3: string;
  duty_4: string;
  duty_5: string;
  duty_6: string;
  duty_7: string;
  duty_8: string;
  qualification: string;
  qualification_other: string;
  if_phd_required: boolean;
  experience: string;
  skill_1: string;
  skill_2: string;
  skill_3: string;
  skill_4: string;
  skill_5: string;
  skill_6: string;
  skill_7: string;
  skill_8: string;
  if_made_redundant: boolean;
  completed: boolean;
  loading: boolean;
  soc_code_eligible_for_outside: boolean;
};

const castValue = (payload: string): string => {
  return payload && payload !== " " ? payload : "―";
};

const castNumber = (payload: number): string => {
  return payload ? payload.toString() : "-";
};

const castPrice = (payload: number, currencySymbol: string): string => {
  return payload ? `${currencySymbol}${payload.toString()}` : "-";
};

const castBoolean = (payload: string | boolean): boolean => {
  if (typeof payload === "boolean") return payload;

  return !!(payload && payload === "true");
};

const convertNullBoolToBool = (section: TNullBool) => {
  return section.Valid && section.Bool;
};

export const convertRoleTemplatesForTable = (a: API_TRoleTemplate): TRoleTemplate_Table => {
  return {
    id: a.id,
    company_id: castNumber(a.company_id),
    creation_user_flow_id: castNumber(a.creation_user_flow_id),
    job_template_title: castValue(a.job_template_title),
    soc_group_number: castNumber(a.soc_group_number),
    soc_group_name: castValue(a.soc_group_name),
    job_title_eligible_for_sw: castBoolean(a.job_title_eligible_for_sw),
    basic_salary: castPrice(a.basic_salary, "£"),
    hours_work: castNumber(a.hours_work),
    job_title_shortage_list_eng: castValue(a.job_title_shortage_list_eng),
    job_title_shortage_list_wal: castValue(a.job_title_shortage_list_wal),
    job_title_shortage_list_scot: castValue(a.job_title_shortage_list_scot),
    job_title_shortage_list_nir: castValue(a.job_title_shortage_list_nir),
    job_title_eligible_for_phd: castBoolean(a.job_title_eligible_for_phd),
    job_title: castValue(a.job_title),
    if_registration_required: castBoolean(a.if_registration_required),
    cos_expected_duration: castNumber(a.cos_expected_duration),
    reporting_to: castValue(a.reporting_to),
    duty_1: castValue(a.duty_1),
    duty_2: castValue(a.duty_2),
    duty_3: castValue(a.duty_3),
    duty_4: castValue(a.duty_4),
    duty_5: castValue(a.duty_5),
    duty_6: castValue(a.duty_6),
    duty_7: castValue(a.duty_7),
    duty_8: castValue(a.duty_8),
    qualification: castValue(a.qualification),
    qualification_other: castValue(a.qualification_other),
    if_phd_required: castBoolean(a.if_phd_required),
    experience: castValue(a.experience),
    skill_1: castValue(a.skill_1),
    skill_2: castValue(a.skill_2),
    skill_3: castValue(a.skill_3),
    skill_4: castValue(a.skill_4),
    skill_5: castValue(a.skill_5),
    skill_6: castValue(a.skill_6),
    skill_7: castValue(a.skill_7),
    skill_8: castValue(a.skill_8),
    if_made_redundant: castBoolean(a.if_made_redundant),
    completed: castBoolean(a.completed),
    loading: false,
    soc_code_eligible_for_outside: convertNullBoolToBool(a.soc_code_eligible_for_outside),
  } as TRoleTemplate_Table;
};

export type TRoleTemplate_Dropdown = {
  label: string;
  value: string;
  soc_group_number: number;
};

export const convertJobTemplatesToSelect = (a: API_TRoleTemplate): TRoleTemplate_Dropdown => {
  return {
    label: a.job_template_title,
    value: a.job_template_title,
    soc_group_number: a.soc_group_number,
  };
};

export type TRoleTemplate_Dropdown_CaseCreation = {
  label: string;
  value: number;
  badge?: string;
  soc_code_eligible_for_outside: boolean;
};

export const isUpdatedWithinMinutes = (updatedAt: string, minutes = 5): boolean => {
  const updatedDate = new Date(updatedAt);
  const now = new Date();

  const diffInMs = now.getTime() - updatedDate.getTime();
  const diffInMinutes = diffInMs / (1000 * 60);

  return diffInMinutes < minutes;
};

export const convertJobTemplatesToSelectCaseCreation = (
  a: API_TRoleTemplate
): TRoleTemplate_Dropdown_CaseCreation => {
  return {
    label: a.job_template_title,
    value: a.id,
    badge: isUpdatedWithinMinutes(a.updated_at, 15) ? "New" : "",
    soc_code_eligible_for_outside: convertNullBoolToBool(a.soc_code_eligible_for_outside),
  };
};
